import { bundledLanguagesInfo } from "shiki"
import { getFilename } from "@opencode-ai/core/util/path"
import type { FilePart, Part, ToolPart } from "@opencode-ai/sdk/v2"

export function attached(part: FilePart) {
  return part.url.startsWith("data:") && !inline(part)
}

export function inline(part: FilePart) {
  return part.source?.text?.start !== undefined && part.source?.text?.end !== undefined
}

export function kind(part: FilePart) {
  return part.mime.startsWith("image/") ? "image" : "file"
}

// language metadata only; grammars stay behind shiki's lazy imports
const LANGUAGE_NAMES = new Map<string, string>(
  bundledLanguagesInfo.flatMap((info) =>
    [info.id, ...(info.aliases ?? [])].map((alias) => [alias, info.name] as [string, string]),
  ),
)

// attachments carry text/plain for all text files, so the label comes from the extension;
// filename may be an absolute path, so extract the basename before looking for one
export function typeLabel(filename: string, mime: string, fallback: string) {
  if (mime === "application/pdf") return "PDF"
  const base = getFilename(filename)
  // idx 0 is a dotfile like .gitignore, not an extension
  const idx = base.lastIndexOf(".")
  const suffix = idx <= 0 ? "" : base.slice(idx + 1).toLowerCase()
  if (!suffix) return fallback
  return LANGUAGE_NAMES.get(suffix) ?? suffix.toUpperCase()
}

const DOWNLOAD_MIMES = new Set([
  "application/pdf",
  "application/octet-stream",
  "application/zip",
  "text/plain",
  "text/csv",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
])

export function downloadAttachment(file: { mime: string; url: string }) {
  if (!DOWNLOAD_MIMES.has(file.mime)) return
  const prefix = `data:${file.mime};base64,`
  if (!file.url.startsWith(prefix)) return
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(file.url.slice(prefix.length))) return
  return file.url
}

export function isTranslationTextTool(
  part: Part,
): part is ToolPart & { tool: "translate-mcp_stream_translate_text" | "translate-mcp_translate_text" } {
  return part.type === "tool" && /^translate-mcp_(stream_translate_text|translate_text)$/.test(part.tool)
}

export function translationText(part: Part): string | undefined {
  if (!isTranslationTextTool(part)) return
  if ("metadata" in part.state && typeof part.state.metadata?.translationText === "string") {
    const value = part.state.metadata.translationText
    if (value.length > 0 && value.length <= 500000) return value
  }
  if (part.state.status !== "completed" || part.state.output.length > 4 * 1024 * 1024) return
  try {
    const value: unknown = JSON.parse(part.state.output)
    if (typeof value !== "object" || value === null || !("result" in value)) return
    const result = value.result
    if (typeof result !== "object" || result === null || !("context" in result)) return
    if (typeof result.context === "string" && result.context.length > 0 && result.context.length <= 500000)
      return result.context
  } catch {
    return
  }
}

export function toolHasProduct(part: Part) {
  if (part.type !== "tool") return false
  if (translationText(part) !== undefined) return true
  if (part.state.status === "error" && /^translate-mcp_/.test(part.tool)) return true
  return (
    part.tool === "translate-mcp_get_document_translation_result" &&
    part.state.status === "completed" &&
    !!part.state.attachments?.some(downloadAttachment)
  )
}

export type TranslationDownload = { uri: string; filename: string; url: string }

export function translationDownloads(parts: readonly Part[]): TranslationDownload[] {
  const files = parts.flatMap((part) => {
    if (
      part.type !== "tool" ||
      part.tool !== "translate-mcp_get_document_translation_result" ||
      part.state.status !== "completed"
    )
      return []
    if (part.state.output.length > 4 * 1024 * 1024) return []
    try {
      const result: unknown = JSON.parse(part.state.output)
      if (!result || typeof result !== "object" || !("resourceUri" in result) || !("filename" in result)) return []
      if (
        typeof result.resourceUri !== "string" ||
        !/^translation-result:\/\/files\/[a-f0-9]{32}$/.test(result.resourceUri)
      )
        return []
      if (typeof result.filename !== "string" || !result.filename || result.filename.length > 1024) return []
      const matches =
        part.state.attachments?.filter((file) => file.filename === result.filename && downloadAttachment(file)) ?? []
      if (matches.length !== 1) return []
      return [{ uri: result.resourceUri, filename: result.filename, url: matches[0]!.url }]
    } catch {
      return []
    }
  })
  // Conflicting deliveries of one resource must never silently select a different file.
  return files.filter(
    (file, index) =>
      files.findIndex((other) => other.uri === file.uri) === index &&
      files.every((other) => other.uri !== file.uri || (other.url === file.url && other.filename === file.filename)),
  )
}

export function translationDownloadReferenced(text: string, uri: string) {
  const prose = text.replace(/(^|\n)[ \t]*(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:\n[ \t]*\2[^\n]*(?=\n|$)|$)/g, "")
  const escaped = uri.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return (
    new RegExp("(?<!`)`" + escaped + "`(?!`)").test(prose) ||
    new RegExp("(?<!!)\\[[^\\]\\n]*\\]\\(" + escaped + "\\)").test(prose)
  )
}
